import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type Column<T> = {
  key: string;
  header: ReactNode;
  render: (row: T, index: number) => ReactNode;
  width?: string;
  align?: "left" | "right" | "center";
  className?: string;
  /** Ô lọc trên đầu cột — hệ cũ gọi là hàng `auxhead`. Có ít nhất một cột khai
   *  báo thì lưới mọc thêm một hàng tiêu đề thứ hai. */
  filter?: ReactNode;
  /** Tiêu đề gộp phía trên: các cột liền nhau cùng `group` nằm chung một ô,
   *  giống tiêu đề hai tầng của hệ cũ ("Tự đánh giá" → Điểm XH / Kết quả XH). */
  group?: string;
};

/**
 * Gộp các cột liền nhau có cùng `group` thành một ô tiêu đề tầng trên. Cột không
 * thuộc nhóm nào thì ô tiêu đề của nó ăn cả hai tầng (`rowSpan = 2`) và không
 * xuất hiện lại ở tầng dưới.
 */
function groupSpans<T>(
  columns: Column<T>[],
): { label: string | null; span: number; from: number }[] {
  const out: { label: string | null; span: number; from: number }[] = [];
  columns.forEach((c, i) => {
    const label = c.group ?? null;
    const last = out[out.length - 1];
    if (last && last.label !== null && last.label === label) last.span += 1;
    else out.push({ label, span: 1, from: i });
  });
  return out;
}

type Props<T> = {
  columns: Column<T>[];
  rows: T[];
  /** index được truyền kèm để danh sách có dòng trùng id vẫn có khoá duy nhất. */
  keyBy: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  empty?: ReactNode;
  dense?: boolean;
  stickyHeader?: boolean;
};

export function DataTable<T>({
  columns,
  rows,
  keyBy,
  onRowClick,
  isLoading,
  empty = "Không có dữ liệu",
  dense,
  stickyHeader,
}: Props<T>) {
  const hasGroups = columns.some((c) => c.group);
  return (
    <div className="relative w-full overflow-x-auto rounded-md border border-border bg-background">
      <table className="min-w-full w-max sm:w-full text-sm">
        <thead
          className={cn(
            "bg-muted/60 text-xxs uppercase tracking-wider font-medium text-muted-foreground",
            stickyHeader && "sticky top-0 z-10",
          )}
        >
          {hasGroups && (
            <tr>
              {groupSpans(columns).map((g) => {
                const c = columns[g.from];
                return (
                  <th
                    key={`g-${c.key}`}
                    colSpan={g.span}
                    rowSpan={g.label === null ? 2 : 1}
                    className={cn(
                      "px-3 text-left font-medium border-b border-border",
                      dense ? "h-9" : "h-11",
                      g.label !== null && "text-center border-l border-border",
                      g.label === null && c.align === "right" && "text-right",
                      g.label === null && c.align === "center" && "text-center",
                      g.label === null && c.className,
                    )}
                    style={g.label === null && c.width ? { width: c.width } : undefined}
                  >
                    {g.label ?? c.header}
                  </th>
                );
              })}
            </tr>
          )}
          <tr>
            {columns
              .filter((c) => !hasGroups || c.group)
              .map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    "px-3 text-left font-medium border-b border-border",
                    dense ? "h-9" : "h-11",
                    c.align === "right" && "text-right",
                    c.align === "center" && "text-center",
                    c.className,
                  )}
                  style={c.width ? { width: c.width } : undefined}
                >
                  {c.header}
                </th>
              ))}
          </tr>
          {columns.some((c) => c.filter) && (
            <tr className="bg-muted/30">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    "px-1.5 py-1 border-b border-border align-middle font-normal",
                    c.className,
                  )}
                >
                  {c.filter ?? null}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-10 text-center text-muted-foreground">
                <span className="inline-block h-3 w-3 rounded-full bg-primary/60 animate-pulse mr-2 align-middle" />
                Đang tải…
              </td>
            </tr>
          )}
          {!isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-12 text-center text-muted-foreground">
                {empty}
              </td>
            </tr>
          )}
          {!isLoading &&
            rows.map((row, i) => (
              <tr
                key={keyBy(row, i)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  "row-hover border-b border-border last:border-b-0 transition-colors",
                  onRowClick && "cursor-pointer",
                )}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn(
                      "px-3 text-foreground whitespace-nowrap",
                      dense ? "h-11 py-2.5 sm:h-10 sm:py-1.5" : "h-12 py-3 sm:py-2",
                      c.align === "right" && "text-right tabular-nums",
                      c.align === "center" && "text-center",
                      c.className,
                    )}
                  >
                    {c.render(row, i)}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
